"use client";

import type { FestivalDetails, ProcessedSet, Review } from "@workspace/types";
import { memo } from "react";
import { AuthPromptModal } from "@/components/auth/auth-prompt-modal";
import { FestivalReviewModal } from "@/components/features/festival/modals/festival-review-modal";
import { SetAnonModal } from "@/components/features/festival/modals/set-anon-modal";
import { SetReviewModal } from "@/components/features/festival/modals/set-review-modal";
import { SetReviewModalReadOnly } from "@/components/features/festival/modals/set-review-modal-read-only";
import type { TabVariant } from "../tabs/use-tab-variant";

interface FollowerInteraction {
	id: string;
	username: string | null;
	display_name: string;
	manual_avatar_url?: string | null;
	google_avatar_url?: string | null;
	rating?: number;
	loved?: boolean;
	must_see?: boolean;
	want_to_see?: boolean;
	saw_live?: boolean;
	saw_stream?: boolean;
	content?: string;
}

interface User {
	id: string;
	email?: string;
	username?: string;
	full_name?: string;
	first_name?: string;
	last_name?: string;
	avatar_url?: string;
}

/**
 * Consolidated modal manager that handles both personal and public festival views
 * Preserves all existing functionality while allowing code reuse
 */
interface FestivalModalManagerProps {
	// Variant configuration
	variant: TabVariant;

	// Core data
	reviewingSet: string | null;
	processedSets: ProcessedSet[];
	socialInteractions: FollowerInteraction[];
	festival: FestivalDetails | null;
	festivalSlug: string;

	// Reviews data
	formattedReviews?: Review[]; // Personal view

	// Callbacks
	onClose: () => void;
	onSave: () => void; // Just trigger save, parent handles everything

	// Authentication/ownership
	isOwnProfile?: boolean; // Personal view
	user?: User | null; // Public view

	// Public view specific props
	authModalOpen?: boolean;
	authModalMessage?: string;
	onAuthModalOpenChange?: (open: boolean) => void;
	reviewingFestival?: boolean;
	onCloseFestivalReview?: () => void;
	onSavedFestivalReview?: () => void;
}

export const FestivalModalManager = memo<FestivalModalManagerProps>(
	({
		variant,
		reviewingSet,
		processedSets,
		festival,
		festivalSlug,
		formattedReviews = [],
		onClose,
		onSave,
		isOwnProfile = false,
		user,
		authModalOpen = false,
		authModalMessage = "",
		onAuthModalOpenChange,
		reviewingFestival = false,
		onCloseFestivalReview,
		onSavedFestivalReview,
	}) => {
		const set = processedSets.find((s) => s.id === reviewingSet) || undefined;
		const review =
			formattedReviews.find((r) => r.setId === reviewingSet) || null;

		// Personal view: Show read-only or editable modal based on ownership
		if (variant === "personal") {
			return (
				<>
					{/* Festival Review Modal - show for personal view when user is authenticated */}
					{user && onCloseFestivalReview && (
						<FestivalReviewModal
							festival={festival}
							isOpen={reviewingFestival}
							onClose={onCloseFestivalReview}
							onSaved={onSavedFestivalReview}
						/>
					)}

					{/* Set Review Modal - show only when reviewing a set */}
					{reviewingSet &&
						(isOwnProfile ? (
							<SetReviewModal
								isOpen={!!reviewingSet}
								set={set}
								festivalSlug={festivalSlug}
								onClose={onClose}
								onSaved={onSave}
								initialReviewData={review}
							/>
						) : (
							<SetReviewModalReadOnly
								isOpen={!!reviewingSet}
								set={set || null}
								review={review}
								festivalSlug={festivalSlug}
								onClose={onClose}
							/>
						))}
				</>
			);
		}

		// Public view: Show auth prompts, festival review, and appropriate set modals
		return (
			<>
				{/* Auth Modal */}
				{onAuthModalOpenChange && (
					<AuthPromptModal
						open={authModalOpen}
						onOpenChange={onAuthModalOpenChange}
						message={authModalMessage}
						actionType="save_sets"
						redirectPath={`/festivals/${festivalSlug}`}
					/>
				)}

				{/* Festival Review Modal - only show when user is authenticated and callback is available */}
				{user && onCloseFestivalReview && (
					<FestivalReviewModal
						festival={festival}
						isOpen={reviewingFestival}
						onClose={onCloseFestivalReview}
						onSaved={onSavedFestivalReview}
					/>
				)}

				{/* Set Review Modal - for authenticated users */}
				{user && reviewingSet && (
					<SetReviewModal
						isOpen={!!reviewingSet}
						set={set}
						festivalSlug={festivalSlug}
						onClose={onClose}
						onSaved={onSave}
						initialReviewData={review}
					/>
				)}

				{/* Anonymous Set Modal - for non-authenticated users */}
				{!user && reviewingSet && (
					<>
						{/* Direct auth prompt for set card clicks */}
						{onAuthModalOpenChange && (
							<AuthPromptModal
								open={!!reviewingSet}
								onOpenChange={(open) => {
									if (!open) {
										onClose(); // Close the reviewing state
									}
									onAuthModalOpenChange(open);
								}}
								actionType="view_details"
								redirectPath={`/festivals/${festivalSlug}`}
							/>
						)}

						{/* SetAnonModal for direct page visits or other contexts */}
						{!onAuthModalOpenChange && (
							<SetAnonModal
								isOpen={!!reviewingSet}
								set={set || null}
								review={review}
								festivalSlug={festivalSlug}
								onClose={onClose}
								onAuthRequired={() => {
									// For direct page visits, we can show a toast or handle differently
									// For now, just close the modal
									onClose();
								}}
							/>
						)}
					</>
				)}
			</>
		);
	},
);

FestivalModalManager.displayName = "FestivalModalManager";
